/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useFeatureFlagEnabled } from 'posthog-js/react'

import IpAllowlistLockoutCard from './IpAllowlistLockoutCard'

import styles from './styles.module.scss'

import { isApiError } from '@/api'
import { fetchUserStatus } from '@/api/enterprise'
import type { UserStatusResponse } from '@/api/generated/api'
import { resendVerificationEmailCaptcha, signInWithEmail, signUpWithEmailCaptcha, verifyEmailOtp } from '@/api/user'
import appleIcon from '@/assets/icons/apple.svg?url'
import googleIcon from '@/assets/icons/google.svg?url'
import microsoftIcon from '@/assets/icons/microsoft.svg?url'
import flowLogo from '@/assets/images/flow-logo.svg?url'
import loginHero from '@/assets/images/login-hero.png'
import Captcha, { CaptchaRef } from '@/components/Captcha'
import { Spinner } from '@/components/Spinner'
import { useToast } from '@/components/Toast/ToastContext'
import { AppDispatch } from '@/store'
import { fetchTeamDomainStatusThunk } from '@/store/features/domain/domainSlice'
import { getEnterpriseThunk, getSubscriptionThunk } from '@/store/features/user/userSlice'
import { isValidEmail, logout, setAuthSession } from '@/utils/auth'
import {
  consumeInvalidAuthCallback,
  createAuthCallbackSource,
  finishAuthCallback,
  getPendingAuthCallbackTokens,
} from '@/utils/authCallback'
import { clearAuthInterruption, getAuthInterruption } from '@/utils/authInterruption'
import { setSentryLoginEmail } from '@/utils/sentry'

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  azure: 'Microsoft',
  workos: 'SSO',
  apple: 'Apple',
  email: 'Email',
  sso: 'SSO',
}

enum LoginSubpage {
  DEFAULT = 'default',
  SSO = 'sso',
  EMAIL = 'email',
  VERIFY = 'verify',
  OTP = 'otp',
}

type LoginAuthContentProps = {
  isIpAllowlistLockout: boolean
  clearLockoutMode: () => void
  subpage: LoginSubpage
  ssoLoginForm: React.ReactNode
  emailSignupForm: React.ReactNode
  verifyForm: React.ReactNode
  otpForm: React.ReactNode
  defaultLoginForm: React.ReactNode
}

const LoginAuthContent = ({
  isIpAllowlistLockout,
  clearLockoutMode,
  subpage,
  ssoLoginForm,
  emailSignupForm,
  verifyForm,
  otpForm,
  defaultLoginForm,
}: LoginAuthContentProps) => {
  if (isIpAllowlistLockout) {
    return <IpAllowlistLockoutCard onRetry={clearLockoutMode} onSignOut={clearLockoutMode} />
  }
  switch (subpage) {
    case LoginSubpage.SSO:
      return ssoLoginForm
    case LoginSubpage.EMAIL:
      return emailSignupForm
    case LoginSubpage.VERIFY:
      return verifyForm
    case LoginSubpage.OTP:
      return otpForm
    default:
      return defaultLoginForm
  }
}

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL
const AUTHKIT_LOGIN_FLAG = 'admin-portal-authkit-login'

const authKitSignInUrl = (provider: string, email?: string) => {
  const params = new URLSearchParams({ source: createAuthCallbackSource() })
  if (email) params.set('email', email)
  return `${BACKEND_URL}/authkit/${provider}/sign_in?${params.toString()}`
}

const legacySignInUrl = (provider: string, email?: string) => {
  const params = new URLSearchParams({ source: createAuthCallbackSource() })
  if (email) params.set('email', email)
  return `${BACKEND_URL}/${provider}/sign_in?${params.toString()}`
}

/** Prefers the API layer's message where it already names the cause; the fallback only says which call failed. */
const toastMessageFor = (error: unknown, fallback: string) =>
  isApiError(error) && (error.isNetworkError || error.status === 429) ? error.message : fallback

/**
 * The precheck produced no usable answer — dropped in transit, or shed by the fail-closed limiter
 * during a Redis blip. It says nothing about the account either way, so don't block the sign-in
 * attempt on it.
 */
const isPrecheckUnavailable = (error: unknown) =>
  isApiError(error) && (error.isNetworkError || error.status === 503) && navigator.onLine !== false

const LoginPage = () => {
  const authKitLoginEnabled = useFeatureFlagEnabled(AUTHKIT_LOGIN_FLAG) === true
  const [authCallbackTokens, setAuthCallbackTokens] = React.useState(getPendingAuthCallbackTokens)
  const hasAuthTokens = !!authCallbackTokens
  const authCallbackStarted = useRef(false)

  const [isLoading, setLoading] = React.useState(hasAuthTokens)
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [fullName, setFullName] = React.useState('')
  const [otpCode, setOtpCode] = React.useState('')
  const [pendingAuthenticationToken, setPendingAuthenticationToken] = React.useState<string | null>(null)
  const [subpage, setSubpage] = React.useState<LoginSubpage>(LoginSubpage.DEFAULT)
  const [isIpAllowlistLockout, setIpAllowlistLockout] = useState(
    () => getAuthInterruption() === 'ip_allowlist_violation'
  )
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const captchaRef = useRef<CaptchaRef>(null)
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { updateToast } = useToast()

  const portalSignInUrl = (provider: string, email?: string) =>
    authKitLoginEnabled ? authKitSignInUrl(provider, email) : legacySignInUrl(provider, email)

  const clearLockoutMode = () => {
    clearAuthInterruption()
    setIpAllowlistLockout(false)
    setSubpage(LoginSubpage.DEFAULT)
  }

  const completeAuthCallback = React.useCallback(() => {
    finishAuthCallback()
    setAuthCallbackTokens(null)
  }, [])

  // A dead session force-signs-out and lands here; without this the form gives no reason why.
  const sessionExpiredNoticeShown = useRef(false)
  useEffect(() => {
    if (sessionExpiredNoticeShown.current || getAuthInterruption() !== 'session_expired') {
      return
    }
    sessionExpiredNoticeShown.current = true
    clearAuthInterruption()
    updateToast('Your session expired. Please sign in again.', 'error')
  }, [updateToast])

  useEffect(() => {
    if (consumeInvalidAuthCallback()) updateToast('Login failed. Please start again.', 'error')
  }, [updateToast])

  const resetCaptcha = () => {
    setCaptchaToken(null)
    captchaRef.current?.reset()
  }

  const handleCaptchaExpire = () => {
    setCaptchaToken(null)
    updateToast('Captcha expired. Please complete it again.', 'error')
  }

  const handleCaptchaError = () => {
    setCaptchaToken(null)
    updateToast('Captcha error. Please try again.', 'error')
  }

  const loginWithGoogle = () => {
    window.location.href = portalSignInUrl('google')
  }

  const loginWithMicrosoft = () => {
    window.location.href = portalSignInUrl('microsoft')
  }

  const loginWithApple = () => {
    window.location.href = portalSignInUrl('apple')
  }

  const handleSSOFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValidEmail(email)) {
      updateToast('Please enter a valid email address.', 'error')
      return
    }
    try {
      setLoading(true)
      setSentryLoginEmail(email)
      const { data } = await fetchUserStatus(email)
      if (!data.exists) {
        setLoading(false)
        // Don't say that we couldn't find the account to avoid user enumeration
        updateToast('Could not find SSO method for this account.', 'error')
        return
      }
      // disabling this check for now, SSO users can also have provider set as google or any prior provider. We might need to think this logic out a bit better
      // this is safe to disable. if their SSO request is indeed invalid, they wont be able to sign in

      // if (data.provider && data.provider !== "workos") {
      //   const providerLabel = PROVIDER_LABELS[data.provider] || data.provider
      //   dispatch(
      //     setBanner({
      //       message: `This account is registered with ${providerLabel}. Please use that provider to sign in.`,
      //       type: "error",
      //     }),
      //   )
      //   return
      // }
      // If provider is SSO or email, continue
      window.location.href = portalSignInUrl('sso', email)
    } catch (error: unknown) {
      if (isPrecheckUnavailable(error)) {
        window.location.href = portalSignInUrl('sso', email)
        return
      }
      setLoading(false)
      updateToast(toastMessageFor(error, 'Failed to check user status. Please try again.'), 'error')
    }
  }

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) {
      updateToast('Please enter both email and password.', 'error')
      return
    }
    if (!isValidEmail(email)) {
      updateToast('Please enter a valid email address.', 'error')
      return
    }
    if (email.includes('+')) {
      updateToast('Email aliases (containing +) are not supported. Using your original email address.', 'error')
      return
    }

    try {
      setLoading(true)
      setSentryLoginEmail(email)
      let status: UserStatusResponse | undefined
      try {
        status = (await fetchUserStatus(email)).data
      } catch (error: unknown) {
        // /email/signin returns sso_required on its own, so an unanswered precheck must not block the password tab.
        if (!isPrecheckUnavailable(error)) {
          throw error
        }
      }
      if (status?.enforce_sso) {
        updateToast('Your organization requires SSO login. Redirecting...', 'success')
        setTimeout(() => {
          window.location.href = portalSignInUrl('sso', email)
        }, 1500)
        return
      }
      if (status && !status.exists) {
        setLoading(false)
        setSubpage(LoginSubpage.EMAIL)
        return
      }
      if (status?.provider && status.provider !== 'email') {
        const providerLabel = PROVIDER_LABELS[status.provider] || status.provider
        setLoading(false)
        updateToast(`This account is registered with ${providerLabel}. Please use that provider to sign in.`, 'error')
        return
      }
      if (status?.exists && !status.verified) {
        setSubpage(LoginSubpage.VERIFY)
        updateToast(
          'Your email requires verification. Check your inbox or use the resend button in the login form.',
          'error'
        )
        setLoading(false)
        return
      }

      const response = await signInWithEmail({
        email,
        password,
        workos_capable: authKitLoginEnabled,
      })
      if (response.data?.verification_method === 'email_otp' && response.data.pending_authentication_token) {
        setPendingAuthenticationToken(response.data.pending_authentication_token)
        setSubpage(LoginSubpage.OTP)
        setLoading(false)
        updateToast('Check your email for a sign-in code.', 'success')
        return
      }
      if (response.data?.error || !response.data?.access_token || !response.data?.refresh_token) {
        // A 200 with an `error` body never reaches the fetchAPI catch, so this failure was invisible.
        Sentry.captureException(new Error('Email signin rejected without tokens'), {
          tags: { reason: 'login_signin_rejected' },
          extra: { backendError: response.data?.error ?? 'missing_tokens' },
        })
        updateToast('Login failed. Please try again.', 'error')
        setLoading(false)
        return
      }

      const { error: sessionError } = await setAuthSession({
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
      })
      if (sessionError) {
        Sentry.captureException(sessionError, {
          tags: { reason: 'login_set_session_failed' },
        })
        updateToast('Login failed. Please try again.', 'error')
        setLoading(false)
        return
      }
      // AuthCheck owns post-auth routing; the auth-state listener refetches the enterprise.
    } catch (error: unknown) {
      setLoading(false)
      if (isApiError(error) && error.detail === 'sso_required') {
        updateToast('Your organization requires SSO login. Redirecting...', 'success')
        setTimeout(() => {
          window.location.href = portalSignInUrl('sso', email)
        }, 1500)
        return
      }
      if (isApiError(error) && error.status === 401) {
        updateToast('Incorrect email or password.', 'error')
        return
      }
      updateToast(toastMessageFor(error, 'Failed to login. Please try again.'), 'error')
    }
  }

  const handleEmailOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pendingAuthenticationToken || !/^\d{6}$/.test(otpCode)) {
      updateToast('Enter the six-digit code from your email.', 'error')
      return
    }

    try {
      setLoading(true)
      const response = await verifyEmailOtp({
        code: otpCode,
        pending_authentication_token: pendingAuthenticationToken,
      })
      if (response.data?.error || !response.data?.access_token || !response.data?.refresh_token) {
        updateToast('That code could not be verified. Request a new code and try again.', 'error')
        setLoading(false)
        return
      }

      const { error: sessionError } = await setAuthSession({
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
      })
      if (sessionError) {
        Sentry.captureException(sessionError, {
          tags: { reason: 'login_set_session_failed' },
        })
        updateToast('Login failed. Please try again.', 'error')
        setLoading(false)
        return
      }
      // AuthCheck owns post-auth routing; the auth-state listener refetches the enterprise.
    } catch (error: unknown) {
      setLoading(false)
      updateToast(toastMessageFor(error, 'That code could not be verified. Try again.'), 'error')
    }
  }

  const handleResendEmailOtp = async () => {
    if (!captchaToken) {
      updateToast('Please complete the captcha.', 'error')
      return
    }
    try {
      setLoading(true)
      const response = await resendVerificationEmailCaptcha({
        email,
        captcha_token: captchaToken,
        workos_capable: true,
      })
      if (response.data?.error) throw new Error(response.data.error)
      updateToast('A new sign-in code was sent.', 'success')
      resetCaptcha()
      setLoading(false)
    } catch (error: unknown) {
      updateToast((error as Error).message || 'Failed to resend the code. Please try again.', 'error')
      resetCaptcha()
      setLoading(false)
    }
  }

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password || !fullName) {
      updateToast('Please fill in all fields.', 'error')
      return
    }

    if (password.length < 6) {
      updateToast('Password must be at least 6 characters long', 'error')
      return
    }

    if (!captchaToken) {
      updateToast('Please complete the captcha.', 'error')
      return
    }

    try {
      setLoading(true)
      const response = await signUpWithEmailCaptcha({
        email,
        password,
        full_name: fullName,
        captcha_token: captchaToken,
      })
      if (response.data?.error) {
        throw new Error(response.data.error)
      }
      if (response.data?.user_id) {
        resetCaptcha()
        setSubpage(LoginSubpage.VERIFY)
        setLoading(false)
      } else {
        throw new Error('Failed to create account. Please try again.')
      }
    } catch (error: unknown) {
      updateToast((error as Error).message || 'Failed to create account. Please try again.', 'error')
      resetCaptcha()
      setSubpage(LoginSubpage.DEFAULT)
      setLoading(false)
    }
  }

  const handleResendVerificationEmail = async () => {
    if (!captchaToken) {
      updateToast('Please complete the captcha.', 'error')
      return
    }

    try {
      setLoading(true)
      const response = await resendVerificationEmailCaptcha({
        email,
        captcha_token: captchaToken,
      })
      if (response.data?.error) {
        throw new Error(response.data.error)
      }
      updateToast('Verification email sent successfully', 'success')
      resetCaptcha()
      setLoading(false)
    } catch (error: unknown) {
      updateToast((error as Error).message || 'Failed to resend verification email. Please try again.', 'error')
      resetCaptcha()
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!authCallbackTokens || authCallbackStarted.current) return
    authCallbackStarted.current = true
    setAuthSession({
      access_token: authCallbackTokens.accessToken,
      refresh_token: authCallbackTokens.refreshToken,
    })
      .then(({ data, error }) => {
        if (error || !data.session) {
          void logout(dispatch)
            .catch(() => undefined)
            .finally(completeAuthCallback)
          updateToast('Login failed. Please try again.', 'error')
          setLoading(false)
        } else {
          completeAuthCallback()
          dispatch(fetchTeamDomainStatusThunk())
          dispatch(getEnterpriseThunk({ needSubscription: false }))
          dispatch(getSubscriptionThunk())
        }
      })
      .catch(async () => {
        await logout(dispatch).catch(() => undefined)
        completeAuthCallback()
        updateToast('Login failed. Please try again.', 'error')
        setLoading(false)
      })
  }, [authCallbackTokens, completeAuthCallback, dispatch, updateToast])

  const ssoLoginForm = (
    <>
      <h2 className={styles.title}>Single Sign-on (SSO)</h2>

      <form className={styles.loginForm} onSubmit={handleSSOFormSubmit}>
        <input
          type="email"
          id="sso-email"
          name="email"
          placeholder="Enter your email"
          className={styles.emailInput}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <div className={styles.ssoBackLink} onClick={() => setSubpage(LoginSubpage.DEFAULT)}>
          Don&apos;t want to use SSO? Back to login options.
        </div>
        <button type="submit" className={styles.ctaButton} disabled={isLoading}>
          Continue
        </button>
      </form>
    </>
  )

  const emailSignupForm = (
    <>
      <h2 className={styles.title}>Sign up with email</h2>

      <form className={styles.loginForm} onSubmit={handleEmailSignup}>
        <input
          type="email"
          id="email-signup-email"
          name="email"
          placeholder="Enter your email"
          className={styles.emailInput}
          value={email}
          disabled
        />
        <input
          type="password"
          id="email-signup-password"
          name="password"
          placeholder="Create a secure password"
          className={styles.emailInput}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <input
          type="text"
          id="email-signup-full-name"
          name="fullName"
          placeholder="Enter your full name"
          className={styles.emailInput}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
        />
        <div
          className={styles.ssoBackLink}
          onClick={() => {
            resetCaptcha()
            setSubpage(LoginSubpage.DEFAULT)
          }}
        >
          Try a different email or login method
        </div>
        <Captcha
          ref={captchaRef}
          onVerify={(token) => setCaptchaToken(token)}
          onExpire={handleCaptchaExpire}
          onError={handleCaptchaError}
        />
        <button type="submit" className={styles.ctaButton} disabled={isLoading}>
          Create account
        </button>
      </form>
    </>
  )

  const verifyForm = (
    <>
      <h2 className={styles.title}>Check your email</h2>
      <div className={styles.instructions}>
        We sent an email to &quot;{email}&quot;
        <ol>
          <li>
            Click &quot;<b>Verify account</b>&quot; to finish signing up. You can do this from any device.
          </li>
          <li>Refresh the admin portal when you&apos;re done and log in.</li>
        </ol>
      </div>
      <div className={styles.verificationContainer}>
        <Captcha
          ref={captchaRef}
          onVerify={(token) => setCaptchaToken(token)}
          onExpire={handleCaptchaExpire}
          onError={handleCaptchaError}
        />
        <button
          className={styles.ctaButton}
          onClick={handleResendVerificationEmail}
          disabled={isLoading || !captchaToken}
        >
          Resend verification email
        </button>
        <button
          className={styles.ctaButton}
          onClick={() => {
            resetCaptcha()
            setSubpage(LoginSubpage.DEFAULT)
          }}
        >
          Back to login options
        </button>
      </div>
    </>
  )

  const otpForm = (
    <>
      <h2 className={styles.title}>Enter your sign-in code</h2>
      <div className={styles.instructions}>We sent a six-digit code to &quot;{email}&quot;.</div>
      <form className={styles.loginForm} onSubmit={handleEmailOtpSubmit}>
        <input
          type="text"
          id="email-otp-code"
          name="code"
          placeholder="Six-digit code"
          className={styles.emailInput}
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
        />
        <Captcha
          ref={captchaRef}
          onVerify={(token) => setCaptchaToken(token)}
          onExpire={handleCaptchaExpire}
          onError={handleCaptchaError}
        />
        <button
          type="button"
          className={styles.ctaButton}
          onClick={handleResendEmailOtp}
          disabled={isLoading || !captchaToken}
        >
          Resend code
        </button>
        <button type="submit" className={styles.ctaButton} disabled={isLoading || otpCode.length !== 6}>
          Verify code
        </button>
        <button
          type="button"
          className={styles.ctaButton}
          onClick={() => {
            setPendingAuthenticationToken(null)
            setOtpCode('')
            resetCaptcha()
            setSubpage(LoginSubpage.DEFAULT)
          }}
        >
          Back to login options
        </button>
      </form>
    </>
  )

  const defaultLoginForm = (
    <>
      <h2 className={styles.title}>Get started</h2>

      <div className={styles.oauthContainer}>
        <button className={styles.oauthButton} onClick={loginWithGoogle}>
          <img src={googleIcon} alt="Google" />
          Continue with Google
        </button>
        <button className={styles.oauthButton} onClick={loginWithMicrosoft}>
          <img src={microsoftIcon} alt="Microsoft" />
          Continue with Microsoft
        </button>
        <button className={styles.oauthButton} onClick={loginWithApple}>
          <img src={appleIcon} alt="Apple" />
          Continue with Apple
        </button>
        <button className={styles.oauthButton} onClick={() => setSubpage(LoginSubpage.SSO)}>
          Single Sign-on (SSO)
        </button>
      </div>

      <div className={styles.divider}>or</div>

      <form className={styles.loginForm} onSubmit={handleEmailLogin}>
        <input
          type="email"
          id="email"
          name="email"
          placeholder="Enter your email"
          className={styles.emailInput}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          id="password"
          name="password"
          placeholder="Enter your password"
          className={styles.emailInput}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <div className={styles.forgotPassword} onClick={() => navigate('/forgot-password')}>
          Forgot password?
        </div>
        <button type="submit" className={styles.ctaButton} disabled={isLoading}>
          Continue
        </button>
      </form>
    </>
  )

  return (
    <>
      <Helmet>
        <title>Wispr Flow | Login</title>
      </Helmet>
      <div className={styles.container}>
        <img src={flowLogo} alt="Wispr Flow" />
        {isLoading ? (
          <Spinner size={32} color="#1a1a1a" />
        ) : (
          <div className={styles.heroWrapper}>
            <img src={loginHero} alt="Login Hero" className={styles.loginHero} />

            <div className={styles.authContainer}>
              <LoginAuthContent
                isIpAllowlistLockout={isIpAllowlistLockout}
                clearLockoutMode={clearLockoutMode}
                subpage={subpage}
                ssoLoginForm={ssoLoginForm}
                emailSignupForm={emailSignupForm}
                verifyForm={verifyForm}
                otpForm={otpForm}
                defaultLoginForm={defaultLoginForm}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default LoginPage
